import angular from 'angular';
import _ from 'lodash-es';
import APIV5 from '~/models/api';
import Attribut from './attribut';
import Droit from './droit';
import Lang from './lang';
import Link from './link';
import Method from './method';
import NodeDefinition from './node-definition';
import Repre from './repre';
import { COMPONENT_OF, COMPOSED_OF, DEFINED_BY } from '../../constants/common-kids';
// eslint-disable-next-line unicorn/prevent-abbreviations
import DroitDef from './droit-def';
// eslint-disable-next-line no-unused-vars
import DroitCust from './droit-cust';
import Lock from './lock';

/**
 * @type {string | null}
 */
let userLang;

export default class Node {
	#$injector;

	/**
	 * List of child nodes
	 *
	 * @type {Node[]}
	 */
	nodes;

	/* @ngInject */
	/**
	 * Instantiates a Node object
	 *
	 * @param {import('angular').auto.IInjectorService} $injector
	 * @param {import('@knitiv/api-client-javascript').KNode | undefined} [kid]
	 * @param {import('@knitiv/api-client-javascript').KNode | undefined} [isa]
	 */
	constructor(
		$injector,
		kid,
		isa,
	) {
		this.#$injector = $injector;

		/**
		 * Backup some data
		 * TODO: should be moved in real protected property
		 *
		 * @type {any}
		 * @protected
		 */
		this.oldData = {
			droit: [],
			kjson: null,
			kndef: new NodeDefinition('kndef'),
			kndefext: new NodeDefinition('kndefext'),
			typeKid: undefined,
		};

		/**
		 * Orders stack
		 * TODO: should be moved in real protected property
		 *
		 * @type {Object.<string, any>}
		 * @protected
		 */
		this.order = {
			isa: undefined,
		};

		/**
		 * Object attributes
		 *
		 * @type {import('./attribut').default[]}
		 */
		this.attribut = [];

		/**
		 * ?
		 *
		 * @type {number}
		 */
		this.cert = 0;

		/**
		 * Object comments
		 *
		 * @type {string[]}
		 */
		this.comment = [];

		/**
		 * List of "component of"
		 *
		 * @type {Link[]}
		 */
		this.componentof = [];

		/**
		 * List of components (from ObjectDef with params.components = true)
		 *
		 * @type {any[]}
		 */
		this.components = [];

		/**
		 * Create flag
		 *
		 * @type {boolean}
		 */
		this.create = false;

		/**
		 * Creation date
		 *
		 * @type {string | undefined}
		 */
		this.datecreation = undefined;

		/**
		 * Start date
		 *
		 * @type {Date | number | undefined}
		 */
		this.dated = undefined;

		/**
		 * End date
		 *
		 * @type {Date | number | undefined}
		 */
		this.datef = undefined;

		/**
		 * Liens par défaut (pour l'import en masse)
		 *
		 * @type {import('@knitiv/api-client-javascript').ObjectDefLink[]}
		 */
		this.defaultLinkValues = [];

		/**
		 * List of rights
		 *
		 * @type {Droit[]}
		 */
		this.droit = [];

		/**
		 * List of customized rights
		 *
		 * @type {DroitCust[]}
		 */
		this.droitcust = [];

		/**
		 * List of default rights to apply to objects
		 *
		 * @type {DroitDef[]}
		 */
		this.droitdef = [];

		/**
		 * Enabled
		 *
		 * @type {0 | 1}
		 */
		this.enabled = 1;

		/**
		 * Icon
		 *
		 * @type {string | undefined}
		 */
		this.icon = undefined;

		/**
		 * @type {any}
		 */
		this.int1 = undefined;

		/**
		 * @typedef Isa
		 * @property {import('@knitiv/api-client-javascript').KNode | undefined} objid - KID of the class.
		 * @property {number | undefined} kright
		 * @property {string | undefined} objname - your name.
		 * @property {Lang} lang - language.
		 */

		/**
		 * "is_a" class identifier
		 *
		 * @type {Isa}
		 */
		this.isa = {
			objid:   isa,
			objname: undefined,
			kright:  undefined,
			lang:    new Lang(),
		};

		/**
		 * "is_a" definition list of each "componentof"
		 *
		 * @type {Array<*>}
		 */
		this.isa_componentof = [];

		/**
		 * "is_a" definition of each "definedby"
		 *
		 * @type {Link[]}
		 */
		this.isa_definedby = [];

		/**
		 * "is_a" definition of each "lang"
		 *
		 * @type {Array<*>}
		 */
		this.isa_lang = [];

		/**
		 * Flag to determine if this node is a file
		 *
		 * @type {boolean}
		 */
		this.isfile = false;

		/**
		 * Definition
		 *
		 * @type {any}
		 */
		this.kdef = undefined;

		/**
		 * JSON additional parameters
		 *
		 * @type {any}
		 */
		this.kjson = null;

		/**
		 * K identifier
		 *
		 * @type {import('@knitiv/api-client-javascript').KNode | undefined}
		 */
		this.kid = kid;

		/**
		 * Node definition
		 *
		 * @type {NodeDefinition}
		 */
		this.kndef = new NodeDefinition('kndef');

		/**
		 * Node custom definition
		 *
		 * @type {NodeDefinition}
		 */
		this.kndefext = new NodeDefinition('kndefext');

		/**
		 * Representation definition
		 *
		 * @type {*}
		 */
		this.krepre = {
			list:        [],
			mode:        undefined,
			name:        undefined,
			object_mode: undefined,
		};

		/**
		 * Node permissions
		 *
		 * @type {number}
		 */
		this.kright = 0;

		/**
		 * Node language
		 *
		 * @type {Lang}
		 */
		this.lang = new Lang();

		/**
		 * Link list
		 *
		 * @type {Link[]}
		 */
		this.link = [];

		/**
		 * Loading flag during call to ObjectInfo API
		 *
		 * @type {boolean}
		 */
		this.loading = false;

		/**
		 * Lock information
		 *
		 * @type {import('./lock').default}
		 */
		this.lock = new Lock();

		/**
		 * List of methods
		 *
		 * @type {Method[]}
		 */
		this.method = [];

		/**
		 * Node name
		 *
		 * @type {string | undefined}
		 * @deprecated
		 */
		this.name = undefined;

		this.nodes = [];

		/**
		 * List of objects
		 *
		 * @type {Node[]}
		 */
		this.object = [];

		/**
		 * Object type
		 *
		 * @type {string | undefined}
		 */
		this.objtype = undefined;

		/**
		 * @typedef Owner
		 * @property {import('@knitiv/api-client-javascript').KNode | undefined} kid Owner kid
		 * @property {string | undefined} name Owner name
		 */

		/**
		 * Node owner information
		 *
		 * @type {Owner}
		 */
		this.owner = {
			kid: undefined,
			name: undefined,
		};

		/**
		 * Node parent
		 *
		 * @type {Node | undefined}
		 */
		this.parent = undefined;

		/**
		 * protected
		 *
		 * @type {0 | 1}
		 */
		this.protected = 0;

		/**
		 * Representation list
		 *
		 * @type {Repre[]}
		 */
		this.repre = [];

		/**
		 * Node type
		 *
		 * @type {import('@knitiv/api-client-javascript').TypeKid}
		 */
		this.type_kid = 'K_NODE;STRING';

		/**
		 * Node type
		 *
		 * @type {string | undefined}
		 */
		this.uploadFile = undefined;

		/**
		 * version_sys
		 *
		 * @type {?number}
		 */
		this.version_sys = null;

		/**
		 * Viewer used to preview file attached to this node
		 *
		 * @type {?string}
		 * @deprecated
		 */
		this.viewer_engine = null;

		/**
		 * Workflow management
		 *
		 * @typedef WorkflowOptions
		 * @property {import('@knitiv/api-client-javascript').KNode} default
		 * @property {boolean} disabled
		 * @property {boolean} eligible Is node eligible to a workflow?
		 * @property {import('@knitiv/api-client-javascript').KNode[] | 'ALL'} list All profiles or profiles-array
		 * @property {boolean} required Is workflow required?
		 */
		this.wf = {
			default:  undefined,
			disabled: false,
			eligible: false,
			list:     'ALL',
			required: false,
		};

		/**
		 * Workflow list
		 *
		 * @type {any[]}
		 */
		this.workflow = [];

		// Set the user lang
		userLang = $injector.get('User')?.getContext().lang[0];

		this.oldData.droit = [];
		this.oldData.kjson = null;
		this.oldData.kndef = new NodeDefinition('kndef');
		this.oldData.kndefext = new NodeDefinition('kndefext');
		this.oldData.typeKid = undefined;
	}

	/**
	 * Adds an order to be executed by ObjectCreate or ObjectModify
	 * This method should be PROTECTED!!
	 *
	 * @param {string} type - type of order property (should be "LINK_LIST", "REPRE_LIST", "DROIT_LIST",
	 *                        "ATTRIBUT_LIST" or "SET")
	 * @param {string} action - operation of the order (should be: "ADD", "MOD" or "DEL")
	 * @param {any} order - order property
	 * @private
	 */
	addOrder(type, action, order) {
		if (!this.loading) {
			const relationKid = (type === 'LINK_LIST' && order.relation_kid) || undefined;
			if (!(type in this.order)) {
				this.order[type] = type === 'LINK_LIST' ? {} : [];
			}

			if (type === 'LINK_LIST' && !(relationKid in this.order[type])) {
				this.order[type][relationKid] = [];
			}

			if (type === 'REPRE_LIST' && !('REPRE_LIST' in this.order)) {
				this.order[type] = [];
			}

			if (!this.create && angular.isObject(order)) {
				order.action = action;
			}

			// fix incoherent ref_role/role_type for DROIT_LIST/DROITDEF_LIST/DROITCUST_LIST
			if ((type === 'DROIT_LIST' || type === 'DROITDEF_LIST' || type === 'DROITCUST_LIST') && 'ref_role' in order) {
				order.role_kid = order.ref_role;
				delete order.ref_role;
			}

			let indexToUpdate = -1;

			// dans tous les cas on recherche si l'element existe !!!
			for (const [k, o] of Object.entries(this.order[type])) {
				if (type === 'ATTRIBUT_LIST' && o.name === order.name) {
					indexToUpdate = Number.parseInt(k, 10);
				}
				else if (type === 'REPRE_LIST' && o.lang === order.lang) {
					indexToUpdate = Number.parseInt(k, 10);
				}
				else if ((type === 'DROIT_LIST' && ((o.org_kid && o.org_kid === order.org_kid
					&& o.role_kid && o.role_kid === order.role_kid) || (o.kid && o.kid === order.kid)))
				) {
					indexToUpdate = Number.parseInt(k, 10);
				}
				else if ((type === 'DROITDEF_LIST' && ((o.org_kid && o.org_kid === order.org_kid
						&& o.role_kid && o.role_kid === order.role_kid) || (o.kid && o.kid === order.kid)))
				) {
					indexToUpdate = Number.parseInt(k, 10);
				}
				else if ((type === 'DROITCUST_LIST' && ((o.org_kid && o.org_kid === order.org_kid
						&& o.role_kid && o.role_kid === order.role_kid) || (o.kid && o.kid === order.kid)))
				) {
					indexToUpdate = Number.parseInt(k, 10);
				}
				else if (type === 'LINK_LIST') {
					indexToUpdate = o.findIndex(
						(/** @type {any} */ o2) => (order.kid !== undefined && o2.kid === order.kid)
							|| (order.kid === undefined && o2.objnum2 === order.objnum2 && o2.linker_kid === order.linker_kid),
					);
				}
			}
			if (indexToUpdate === -1) {
				// eslint-disable-next-line no-param-reassign
				action = 'ADD';
			}

			switch (action) {
			case 'MOD': {
				if (type === 'LINK_LIST') {
					order.action = this.order[type][relationKid][indexToUpdate].action;
					this.order[type][relationKid][indexToUpdate] = order;
				}
				else {
					order.action = this.order[type][indexToUpdate].action;
					this.order[type][indexToUpdate] = order;
				}
				break;
			}

			case 'DEL': {
				if (type === 'LINK_LIST') {
					this.order[type][relationKid].splice(indexToUpdate, 1);
					if (this.order[type][relationKid].length === 0) {
						delete this.order[type][relationKid];
					}
				}
				else if (this.order[type][indexToUpdate]) {
					// Check if an entry already exist
					// And modify the action to DEL
					// Use case: you modify an entry, then you delete it
					this.order[type][indexToUpdate].action = 'DEL';
				}
				else {
					this.order[type].splice(indexToUpdate, 1);
					if (this.order[type].length === 0) {
						delete this.order[type];
					}
				}
				break;
			}

			case 'SET': {
				this.order[type] = order;
				break;
			}

			default: {
				if (indexToUpdate === -1) {
					// new order = simple add
					if (type === 'LINK_LIST') {
						this.order[type][relationKid].push(order);
					}
					else {
						this.order[type].push(order);
					}
				}
			}
			}
		}
	}

	/**
	 * Delete an order to be executed by ObjectCreate or ObjectModify
	 *
	 * @param {string} type - type of order property (should be "LINK_LIST", "REPRE_LIST", "DROIT_LIST",
	 *                        "ATTRIBUT_LIST" or "SET")
	 * @param {any} order - order property
	 * @private
	 */
	delOrder(type, order) {
		if (type in this.order) {
			let indexToDelete = -1;
			for (const [k, o] of this.order[type].entries()) {
				if (type === 'ATTRIBUT_LIST' && o.name === order.name) {
					indexToDelete = k;
				}
				if (type === 'REPRE_LIST' && o.lang.kid === order.lang.kid) {
					indexToDelete = k;
				}
				if (type === 'DROIT_LIST' && o.role_kid === order.role_kid) {
					indexToDelete = k;
				}
				if (type === 'DROITDEF_LIST' && o.org_kid === order.org_kid && o.role_kid === order.role_kid
					&& o.order === order.order && o.suborder === order.suborder) {
					indexToDelete = k;
				}
				if (type === 'DROITCUST_LIST' && o.org_kid === order.org_kid && o.role_kid === order.role_kid
					&& o.order === order.order && o.suborder === order.suborder) {
					indexToDelete = k;
				}
				if (type === 'LINK_LIST' && o.kid === order.kid) {
					indexToDelete = k;
				}
			}

			if (indexToDelete >= 0) {
				this.order[type].splice(indexToDelete, 1);
				if (this.order[type].length === 0) {
					delete this.order[type];
				}
			}
		}
	}

	/**
	 * Get node attribute
	 *
	 * @param {any | Attribut} attribut
	 * @returns {Node} The current node
	 */
	addAttribute(attribut) {
		if (!(attribut instanceof Attribut)) {
			// eslint-disable-next-line no-param-reassign
			attribut = new Attribut(attribut);
		}

		/** @type {any} */
		const order = {
			name:     attribut.name,
			ref_type: attribut.ref_type,
		};

		if (attribut.ref_type === 'KEPLER_TYPE;TEXT') {
			order.data1 = attribut.value ?? '';
		}
		else if (attribut.ref_type === 'KEPLER_TYPE;JSON') {
			order.data1 = angular.toJson(attribut.value);
		}
		else {
			order.value = attribut.value;
		}
		this.addOrder('ATTRIBUT_LIST', 'ADD', order);

		this.attribut.push(attribut);

		return this;
	}

	/**
	 * Adds a node right
	 *
	 * @param {any | Droit} droit instance or non-instanciated object
	 * @returns {Node} The current node
	 */
	addDroit(droit) {
		if (!(droit.constructor === Droit)) {
			// eslint-disable-next-line no-param-reassign
			droit = new Droit(droit);
		}

		if ((!droit.kid || !(this.droit.some(d => droit.kid && d.kid === droit.kid)))
			&& !(this.droit.some(d => d.org.kid === droit.org.kid && d.role.kid === droit.role.kid))
		) {
			this.addOrder('DROIT_LIST', 'ADD', {
				org_kid:  droit.org.kid,
				role_kid: droit.role.kid,
				kright:   droit.kright,
			});

			this.droit.push(droit);
		}

		return this;
	}

	/**
	 * Adds a node custom right
	 *
	 * @param {any | DroitCust} droitcust instance or non-instanciated object
	 * @returns {Node} The current node
	 */
	addDroitCust(droitcust) {
		if (droitcust.constructor !== DroitCust) {
			// eslint-disable-next-line no-param-reassign
			droitcust = new DroitCust(droitcust);
		}

		if (
			droitcust.order
			&& (!droitcust.kid || !(this.droitcust.some(d => droitcust.kid && d.kid === droitcust.kid)))
			&& (!(this.droitcust.some(
				d => d.order === droitcust.order
					&& d.suborder === droitcust.suborder
					&& d.org.kid === droitcust.org.kid
					&& d.role.kid === droitcust.role.kid
			)))
		) {
			this.addOrder('DROITCUST_LIST', 'ADD', droitcust.export());

			this.droitcust.push(droitcust);
		}

		return this;
	}

	/**
	 * Adds a node org right
	 *
	 * @param {any | DroitDef} droitdef instance or non-instanciated object
	 * @returns {Node} The current node
	 */
	addDroitDef(droitdef) {
		if (droitdef.constructor !== DroitDef) {
			// eslint-disable-next-line no-param-reassign
			droitdef = new DroitDef(droitdef);
		}

		if (
			droitdef.order
			&& (!droitdef.kid || !(this.droitdef.some(d => droitdef.kid && d.kid === droitdef.kid)))
			&& (!(this.droitdef.some(
				d => d.order === droitdef.order
					&& d.suborder === droitdef.suborder
					&& d.org.kid === droitdef.org.kid
					&& d.role.kid === droitdef.role.kid
			)))
		) {
			this.addOrder('DROITDEF_LIST', 'ADD', droitdef.export());

			this.droitdef.push(droitdef);
		}

		return this;
	}

	/**
	 * Adds an object in the class
	 *
	 * @param {import('@knitiv/api-client-javascript').KNode} kid KID identifier
	 * @param {import('@knitiv/api-client-javascript').KNode} isa ISA class identifier
	 * @returns {Node} The new node
	 */
	addNode(
		kid,
		isa,
	) {
		const node = new Node(this.#$injector, kid, isa);
		this.nodes.push(node);
		node.parent = this;

		return node;
	}

	/**
	 * Adds a relation
	 *
	 * @param {import('@knitiv/api-client-javascript').KNode} relationKid
	 * @param {Node} node
	 * @param {import('@knitiv/api-client-javascript').LinkerKNode} [linkerKid]
	 * @param {import('@knitiv/api-client-javascript').KNode} [classKid]
	 * @returns {Node} The current node
	 */
	addRelation(
		relationKid,
		node,
		linkerKid = undefined,
		classKid = undefined,
	) {
		const link = new Link({
			relation_kid: relationKid,
			class_kid:    classKid || node.isa.objid,
			objnum2_kid:  node.kid,
			isa:          node.isa.objid,
			linker_kid:   linkerKid,
			flow:         'from',
		});

		/** @type {any} */
		const order = {
			relation_kid: link.relation_kid,
			class_kid:    link.class_kid,
			linker_kid:   linkerKid,
			objnum2:      link.objnum2_kid,
			objnum2_isa:  link.isa,
			action:       'ADD',
		};

		this.addOrder('LINK_LIST', 'ADD', order);

		this.link.push(link);

		return this;
	}

	/**
	 * @param {Date} [date]
	 * @param {import('@knitiv/api-client-javascript').LinkerKNode} linkerKid
	 */
	addRelationDate(date = undefined, linkerKid = '0') {
		const stringDate = (date ?? new Date()).toISOString();
		const nodeDate = this.addNode(`K_NODE;${stringDate.replaceAll(/[ .:TZ-]/g, '')}`, 'K_NODE;DATE');
		nodeDate.type_kid = 'K_NODE;DATE';
		nodeDate.addRepre(new Repre({
			type:  'K_NODE;DATE',
			value: stringDate,
		}));
		this.addRelation(COMPOSED_OF, nodeDate, linkerKid);
	}

	/**
	 * Add a representation object
	 *
	 * @param {Repre} repre Representation instance
	 * @returns {Node} The current node
	 */
	addRepre(repre) {
		this.addOrder('REPRE_LIST', 'ADD', repre.exportForObjectSet());
		let isIn = false;
		for (const r of this.repre) {
			if (repre.lang.kid === r.lang.kid) {
				isIn = true;
			}
		}
		if (!isIn) {
			this.repre.push(repre);
		}

		return this;
	}

	/**
	 * Checks a node right
	 *
	 * @param {number} droit
	 * @returns {boolean} Result
	 */
	checkDroit(droit) {
		// eslint-disable-next-line no-bitwise
		return (this.kright & droit) > 0; // jshint ignore:line
	}

	/**
	 * Copies the link preview to the clipboard
	 *
	 * @returns {boolean} Successful copy then true
	 */
	copyLinkPreview() {
		const $copyToClipboard = this.#$injector.get('$copyToClipboard');
		return $copyToClipboard(this.getLinkPreview(), true);
	}

	/**
	 * Delete node attribute
	 *
	 * @param {string} name
	 * @returns {Node} The current node
	 */
	delAttribute(name) {
		let indexToDelete = -1;
		for (const [k, attribut] of this.attribut.entries()) {
			if (attribut.name === name) {
				indexToDelete = k;
				this.addOrder('ATTRIBUT_LIST', 'DEL', {
					kid: attribut.kid,
				});
			}
		}
		if (indexToDelete >= 0) {
			this.attribut.splice(indexToDelete, 1);
		}

		return this;
	}

	/**
	 * Deletes a node right
	 *
	 * @param {Droit} droit
	 * @returns {Node} The current node
	 */
	delDroit(droit) {
		for (const d of this.droit) {
			if (d.role.kid === droit.role.kid && d.org.kid === droit.org.kid) {
				this.addOrder('DROIT_LIST', 'DEL', {
					role_kid: d.role.kid,
					org_kid:  d.org.kid,
					kid:      d.kid,
				});
			}
		}

		_.remove(
			this.droit,
			d => d.constructor === droit.constructor && d.role.kid === droit.role.kid && d.org.kid === droit.org.kid,
		);

		return this;
	}

	/**
	 * Deletes a node right
	 *
	 * @param {DroitCust} droitcust
	 * @returns {Node} The current node
	 */
	delDroitCust(droitcust) {
		if (droitcust.kid) {
			this.addOrder('DROITCUST_LIST', 'DEL', {
				kid: droitcust.kid,
			});

			_.remove(
				this.droitcust,
				d => d.kid === droitcust.kid,
			);
		}

		return this;
	}

	/**
	 * Deletes a node right
	 *
	 * @param {DroitDef} droitdef
	 * @returns {Node} The current node
	 */
	delDroitDef(droitdef) {
		if (droitdef.kid) {
			this.addOrder('DROITDEF_LIST', 'DEL', {
				kid: droitdef.kid,
			});

			_.remove(
				this.droitdef,
				d => d.kid === droitdef.kid,
			);
		}

		return this;
	}

	/**
	 * Removes a relation
	 *
	 * @param {any} link
	 * @returns {Node} The current node
	 */
	delRelation(link) {
		this.addOrder('LINK_LIST', 'DEL', {
			action:       'DEL',
			class_kid:    link.class_kid,
			kid:          link.kid,
			linker_kid:   link.linker_kid,
			objnum2:      link.objnum2 ?? link.objnum2_kid,
			relation_kid: link.relation_kid,
		});

		_.remove(
			this.link,
			l => (link.kid !== undefined && l.kid === link.kid)
				|| (
					link.kid === undefined
					&& l.relation_kid === link.relation_kid
					&& l.objnum2_kid === link.objnum2_kid
					&& l.linker_kid === link.linker_kid
				),
		);

		return this;
	}

	/**
	 * Deletes a representation object
	 *
	 * @param {string} langKid - Language KID
	 * @returns {Node} The current node
	 */
	delRepre(langKid) {
		let indexToDelete = -1;
		for (const [k, repre] of this.repre.entries()) {
			if (repre.lang.kid === langKid) {
				indexToDelete = k;
				if (repre.kid) {
					this.addOrder('REPRE_LIST', 'DEL', {
						kid:  repre.kid,
						lang: repre.lang,
					});
				}
				else {
					this.delOrder('REPRE_LIST', repre);
				}
			}
		}
		if (indexToDelete >= 0) {
			this.repre.splice(indexToDelete, 1);
		}

		return this;
	}

	async fetchLinks() {
		if (this.kid) {
			const result = await APIV5.instance.objectToMe({
				node_kid:    this.kid,
				result_mode: 'LINK',
			}, {
				id: 'node.fetch-links',
			});
			for (const l of result.links) {
				this.link.push(new Link({ ...l, flow: 'to' }));
			}
		}
	}

	/**
	 * Get a node attribute by name
	 *
	 * @param {string} name - Name of the attribute
	 * @returns {Attribut | undefined} The attribute instance
	 */
	getAttribute(name) {
		return this.attribut.find(attribut => attribut.name === name);
	}

	/**
	 * @param {?import('@knitiv/api-client-javascript').KNode} relation
	 * @param {?import('@knitiv/api-client-javascript').KNode} isa
	 * @param {import('@knitiv/api-client-javascript').LinkerKNode} [linker]
	 * @returns {Link | undefined}
	 */
	getLink(relation, isa, linker = '0') {
		return this.link.find(link => link.relation_kid === relation && link.isa === isa && link.linker_kid === linker);
	}

	/**
	 * Get the link to preview form/node
	 *
	 * @returns {string} The link to preview form/node
	 */
	getLinkPreview() {
		const $window = this.#$injector.get('$window');
		return `${$window.location.protocol}//${$window.location.host}/preview/${this.kid}`;
	}

	hasChanged() {
		return ('LINK_LIST' in this.order && Object.keys(this.order.LINK_LIST).length > 0)
			|| ('ATTRIBUT_LIST' in this.order && Object.keys(this.order.ATTRIBUT_LIST).length > 0)
			|| ('DROIT_LIST' in this.order && Object.keys(this.order.DROIT_LIST).length > 0)
			|| ('DROITDEF_LIST' in this.order && Object.keys(this.order.DROITDEF_LIST).length > 0)
			|| ('REPRE_LIST' in this.order && Object.keys(this.order.REPRE_LIST).length > 0)
			|| !_.isEqual(this.kjson, this.oldData.kjson)
			|| !_.isEqual(this.kndef, this.oldData.kndef)
			|| !_.isEqual(this.kndefext, this.oldData.kndefext)
			|| this.type_kid !== this.oldData.typeKid;
	}

	/**
	 * @param {boolean} create
	 * @param {import('@knitiv/api-client-javascript').KNode} kid
	 * @returns {*} Object List
	 */
	makeObject(create, kid) {
		/** @type {any} */
		const objectList = {};

		if (this.kid) {
			objectList[this.kid] = _.merge(this.order, { isa: this.isa.objid });

			if (!create && !this.create && this.kid === kid) {
				objectList[this.kid].action = 'MOD';
			}

			if (
				this.kjson
				&& (
					!angular.equals(this.kjson, this.oldData.kjson)
					|| typeof this.kjson !== 'string'
				)
			) {
				objectList[this.kid].kjson = angular.toJson(this.kjson);
			}

			if (this.kndef && !angular.equals(this.kndef, this.oldData.kndef)) {
				objectList[this.kid].kndef = this.kndef;
			}

			if (this.kndefext && !angular.equals(this.kndefext, this.oldData.kndefext)) {
				objectList[this.kid].kndefext = this.kndefext;
			}
		}

		return objectList;
	}

	/**
	 * Update node attribute
	 *
	 * @param {any | Attribut} attribut - Name of the attribute to modify
	 * @returns {Node} The current node
	 */
	modAttribute(attribut) {
		/** @type {any} */
		const order = {
			kid:      attribut.kid,
			name:     attribut.name,
			ref_type: attribut.ref_type,
		};

		if (attribut.ref_type === 'KEPLER_TYPE;TEXT') {
			order.data1 = attribut.value ?? '';
		}
		else if (attribut.ref_type === 'KEPLER_TYPE;JSON') {
			order.data1 = angular.toJson(attribut.value);
		}
		else {
			order.value = attribut.value;
		}
		this.addOrder('ATTRIBUT_LIST', 'MOD', order);

		return this;
	}

	/**
	 * Gets the modified state of the node
	 *
	 * @returns {boolean} True if the node is in "modified" state
	 */
	isModified() {
		return Object.keys(this.order).length > 1 || this.order.isa !== undefined;
	}

	/**
	 * Updates a node right
	 *
	 * @param {string} referenceRole
	 * @param {number} kright
	 * @returns {Node} The current node
	 */
	modDroit(referenceRole, kright) {
		for (const droit of this.droit) {
			if (droit.constructor === Droit && droit.role.kid === referenceRole) {
				droit.kright = kright;
				this.addOrder('DROIT_LIST', 'MOD', {
					kid:      droit.kid,
					role_kid: droit.role.kid,
					kright:   droit.kright,
				});
			}
		}

		return this;
	}

	/**
	 * Updates a node right
	 *
	 * @param {DroitCust} droitcust
	 * @returns {Node} The current node
	 */
	modDroitCust(droitcust) {
		this.addOrder('DROITCUST_LIST', 'MOD', droitcust.export());

		return this;
	}

	/**
	 * Updates a node right
	 *
	 * @param {DroitDef} droitdef
	 * @returns {Node} The current node
	 */
	modDroitDef(droitdef) {
		this.addOrder('DROITDEF_LIST', 'MOD', droitdef.export());

		return this;
	}

	/**
	 * Updates a relation
	 *
	 * @param {Link} link
	 * @returns {Node} The current node
	 */
	modRelation(link) {
		const order = {
			action:       'MOD',
			card_from:    link.card_from,
			card_to:      link.card_to,
			class_kid:    link.class_kid || link.isa,
			data:         link.data,
			kid:          link.kid,
			linker_kid:   link.linker_kid,
			lock:         link.lock,
			objnum2:      link.objnum2_kid,
			objnum2_isa:  link.isa,
			order:        link.order,
			relation_kid: link.relation_kid,
		};

		this.addOrder('LINK_LIST', 'MOD', order);

		return this;
	}

	/**
	 * Updates a representation object
	 *
	 * @param {import('@knitiv/api-client-javascript').KNode} langKid - Language KID
	 * @param {Repre} r - Repre instance of simple object
	 * @returns {Node} The current node
	 */
	modRepre(langKid, r) {
		const rInstance = r instanceof Repre ? r : new Repre(r);
		for (const [k, repre] of this.repre.entries()) {
			if (repre.lang.kid === langKid && rInstance.value !== undefined) {
				this.delOrder('REPRE_LIST', repre);
				if (repre.kid || this.create) {
					this.addOrder('REPRE_LIST', 'MOD', rInstance.exportForObjectSet());
				}
				this.repre[k] = rInstance;
			}
		}

		return this;
	}

	/**
	 * Gets the main name of the node according to current language
	 *
	 * @param {boolean} fromRepre Gets the name from repre array only
	 * @returns {string | undefined} The main name of the node
	 */
	getName(fromRepre = false) {
		let name = fromRepre ? undefined : this.name;

		if (!name) {
			for (const repre of this.repre) {
				if (repre.lang.kid === 'K_NODE;UNIVERSEL') {
					name = repre.value || undefined;
				}
			}
		}

		if (!name) {
			for (const repre of this.repre) {
				if (repre.lang.isa === 'K_NODE;LANGUE' && repre.lang.kid === userLang) {
					name = repre.value || undefined;
				}
			}
		}

		if (!name) {
			for (const repre of this.repre) {
				if (repre.lang.isa === 'K_NODE;LANGUE') {
					name = repre.value || undefined;
				}
			}
		}

		if (!name) {
			for (const repre of this.repre) {
				if (!name) {
					name = repre.value || undefined;
				}
			}
		}

		return name;
	}

	/**
	 * Returns internal order object
	 *
	 * @returns {Object.<string, Object>} Result
	 */
	getOrder() {
		return this.order;
	}

	/**
	 * Gets the representation object according to lang parameter
	 *
	 * @param {string} langKid Language KID
	 * @returns {(null|Repre)} Repre instance
	 */
	getRepre(langKid) {
		let returnValue = Object.create(null);
		if (!langKid) {
			// eslint-disable-next-line no-param-reassign
			langKid = 'K_NODE;UNIVERSEL';
		}
		for (const repre of this.repre) {
			if (repre.lang.kid === langKid) {
				returnValue = repre;
			}
		}

		return returnValue;
	}

	reset() {
		this.create = false;
		this.order = {};

		this.oldData.droit = angular.copy(this.droit);
		this.oldData.kjson = angular.copy(this.kjson);
		// /!\ Change at your own risks                      // allow duplicating the whole class instance
		this.oldData.kndef = new NodeDefinition('kndef', angular.copy(this.kndef));
		this.oldData.kndefext = new NodeDefinition('kndefext', angular.copy(this.kndefext));
		this.oldData.typeKid = this.type_kid;
	}

	/**
	 * @param {any} result
	 * @param {any} [definition]
	 * @param {boolean} [admin]
	 * @param {boolean} [updateDroits]
	 */
	set(result, definition = null, admin = false, updateDroits = true) {
		this.loading = true;

		if (result) {
			for (const [kid, attribute] of Object.entries(result.attribut.list)) {
				attribute.kid = kid;
				this.addAttribute(attribute);
			}

			this.type_kid = result.type_kid;
			this.cert = result.cert;
			this.comment = result.comment;
			if (result.components) {
				this.components = Array.isArray(result.components)
					? result.components
					: Object.entries(result.components)
						.map(([k, component]) => ({
							...component,
							kid: `KLINK;${k}`
						}));
			}

			for (const [kid, l] of Object.entries(result.componentof.list)) {
				this.componentof.push(new Link({ kid, ...l }));
			}
			this.datecreation = result.datecreation;

			if (result.dated) {
				// noinspection JSUnresolvedVariable
				this.dated = (result.kdated === '-99999999999999999' ? Number.NEGATIVE_INFINITY : new Date(result.dated));
			}

			if (result.datef) {
				// noinspection JSUnresolvedVariable
				this.datef = (result.kdatef === '99999999999999999' ? Number.POSITIVE_INFINITY : new Date(result.datef));
			}

			if (result.defaultLinkValues) {
				this.defaultLinkValues = result.defaultLinkValues;
			}

			if (updateDroits) {
				if (result.droit) {
					for (const [kid, droit] of Object.entries(result.droit.list)) {
						droit.kid = kid;
						this.addDroit(droit);
					}
				}

				if (result.droitcust) {
					for (const [kid, droitcust] of Object.entries(result.droitcust.list)) {
						droitcust.kid = kid;
						this.addDroitCust(droitcust);
					}
				}

				if (result.droitdef) {
					for (const [kid, droitdef] of Object.entries(result.droitdef.list)) {
						droitdef.kid = kid;
						this.addDroitDef(droitdef);
					}
				}
			}

			this.enabled = result.enabled ?? 1;

			if (result.lang) {
				this.lang = new Lang(result.lang);
			}

			this.lastForm = result.lastForm === true;

			this.isa.objid = result.isa.objid;
			this.isa.objname = result.isa.objname;
			this.isa.lang = new Lang(result.isa.lang);

			this.isa_componentof = result.isa_componentof.list;
			for (const [kid, l] of Object.entries(result.isa_definedby.list)) {
				this.isa_definedby.push(new Link({ kid, ...l }));
			}

			this.isa_lang.push(...Object.values(result.isa_lang.list));

			this.isfile = result.isfile === 1;

			if (typeof result.kdef === 'string') {
				try {
					this.kdef = JSON.parse(result.kdef);
				}
				catch {
					this.kdef = result.kdef;
				}
			}
			else {
				this.kdef = result.kdef;
			}

			if (typeof result.kjson === 'string') {
				try {
					this.kjson = JSON.parse(result.kjson);
				}
				catch {
					this.kjson = result.kjson;
				}
			}
			else {
				this.kjson = result.kjson;
			}

			this.kid = result.objid;

			if (result.kndef?.CLASS?.DICO !== undefined && Array.isArray(result.kndef.CLASS.DICO)) {
				result.kndef.CLASS.DICO = {};
			}

			// eslint-disable-next-line no-underscore-dangle
			_.merge(this.kndef, new NodeDefinition('kndef', admin ? result._kndef : result.kndef));
			if (!this.kndef.id) {
				this.kndef.id = this.#$injector.get('User').getRef().slice(-12);
				this.kndef.fixId();
			}

			if (admin) {
				// eslint-disable-next-line no-underscore-dangle
				_.merge(this.kndefext, new NodeDefinition('kndefext', result._kndefext));
				if (!this.kndefext.id) {
					this.kndefext.id = this.kndef.id;
					this.kndefext.fixId();
				}
			}

			this.krepre.list = result.krepre_list;
			this.krepre.mode = result.krepre_mode;
			this.krepre.name = result.krepre_name;
			this.krepre.object_mode = result.krepre_object_mode;
			this.kright = result.kright;

			for (const [flow, gl] of Object.entries(result.link.list)) {
				for (const [relationKid, l2] of Object.entries(gl)) {
					if (relationKid !== DEFINED_BY && relationKid !== COMPONENT_OF) {
						for (const [kid, l] of Object.entries(l2.list)) {
							const link = new Link({
								flow,
								kid,
								relation_kid: relationKid,
								...l,
							});
							this.link.push(link);
						}
					}
				}
			}

			if (definition && this.kdef) {
				for (const [dicoId, kdef] of Object.entries(this.kdef)) {
					this.link.push(new Link({
						flow: 'from',
						...kdef,
						kid:  `KLINK;${dicoId}`,
					}));
				}
			}
			else {
				for (const /** @type {'kndef' | 'kndefext'} */ sKndef of ['kndef', 'kndefext']) {
					// @ts-ignore
					const kndef = this[sKndef];
					if (kndef?.CLASS?.DICO) {
						for (const [dicoId, dico] of Object.entries(kndef.CLASS.DICO)) {
							const link = new Link({
								flow:         'from',
								kid:          `KLINK;${dicoId}`,
								linker_kid:   dico.linker_kid,
								linker_info:  dico.linker_info,
								objname:      dico.objname,
								objnum2_kid:  dico.objnum2_kid,
								relation_kid: dico.relation_kid,
							});

							// if (!dico.objnum2_info) {
							// 	console.warn(`Missing "objnum2_info" key in ${sKndef}.CLASS.DICO['${dicoId}']`);
							// }

							let passLink = true;
							for (const common of kndef.CLASS.COMMON) {
								if (common.type === 'link' && common.dico_id === dicoId) {
									link.data = common.data;
									link.card_from = common.card_from;
									link.card_to = common.card_to;
								}
							}
							for (const l of this.link) {
								if (
									l.objnum2_kid === link.objnum2_kid
									&& l.relation_kid === link.relation_kid
									&& l.flow === link.flow
									&& l.linker_kid === link.linker_kid
								) {
									passLink = false;
								}
							}
							if (passLink) {
								this.link.push(link);
							}
						}
					}
				}
			}

			this.lock = new Lock(result.lock);

			if (Array.isArray(result.method?.list)) {
				this.method.push(...result.method.list.map((/** @type {any} */ m) => new Method(m)));
			}

			this.name = result.objname;

			this.objtype = result.objtype;
			this.owner.kid = result.ref_propri;
			this.owner.name = result.ref_propri_name;

			this.protected = result.protected ?? 0;

			if (result.repre?.list) {
				for (const [kid, r] of Object.entries(result.repre.list)) {
					this.addRepre(new Repre({
						lang:  r.lang,
						kid,
						type:  r.type_kid,
						value: r.value,
					}));
				}
			}

			this.type_kid = result.type_kid;
			this.uploadFile = result.uploadFile;

			this.version_sys = result.version_sys;

			// @ts-ignore
			if (!this.#$injector.get('$rootScope').kds && result.viewer_engine) {
				this.viewer_engine = result.viewer_engine;
			}

			this.wf = result.wf;

			// backup properties in oldData
			this.oldData.droit = angular.copy(this.droit);
			this.oldData.kjson = angular.copy(this.kjson);
			// /!\ Change at your own risks                      // allow duplicating the whole class instance
			this.oldData.kndef = new NodeDefinition('kndef', angular.copy(this.kndef));
			this.oldData.kndefext = new NodeDefinition('kndefext', angular.copy(this.kndefext));
			this.oldData.typeKid = this.type_kid;
		}

		this.loading = false;
	}

	/**
	 * Set the Node kid
	 *
	 * @param {import('@knitiv/api-client-javascript').KNode} kid The kid to set
	 */
	setKid(kid) {
		this.kid = kid;
	}

	/**
	 * @param {import('@knitiv/api-client-javascript').KNode} isa
	 */
	setIsa(isa) {
		this.isa.objid = isa;
		this.order.isa = isa;
	}

	/**
	 * Sets the main name of the node
	 *
	 * @param {?string} name - The name to settle
	 * @returns {Node} The current node
	 */
	setName(name) {
		let isSet = false;

		// search for universal repre
		for (const repre of this.repre) {
			if (repre.lang.kid === 'K_NODE;UNIVERSEL') {
				repre.value = name;
				this.modRepre('K_NODE;UNIVERSEL', repre);
				isSet = true;
			}
		}
		//
		// // search for the 1st required repre
		// if (!isSet) {
		// 	for (const repre of this.repre) {
		// 		if (repre.required && !isSet) {
		// 			repre.value = name;
		// 			this.modRepre(repre.lang.kid, repre);
		// 			isSet = true;
		// 		}
		// 	}
		// }

		// search for the 1st repre
		if (!isSet) {
			for (const repre of this.repre) {
				if (!isSet && repre.lang.kid) {
					repre.value = name;
					this.modRepre(repre.lang.kid, repre);
					isSet = true;
				}
			}
		}

		if (!isSet) {
			// if no repre exists then create universal repre
			this.addRepre(new Repre({
				value: name,
			}));
		}

		return this;
	}

	/**
	 * Sets internal order object
	 *
	 * @param {any} order order object to set
	 */
	setOrder(order) {
		this.order = order;
	}

	/**
	 * Sets the type of the node
	 *
	 * @param {import('@knitiv/api-client-javascript').TypeKid} typeKid - The new type
	 * @returns {Node} The current node
	 */
	setTypeKid(typeKid) {
		if (!typeKid) {
			// eslint-disable-next-line no-param-reassign
			typeKid = this.type_kid;
		}

		if (typeKid === this.oldData.typeKid) {
			delete this.order.type_kid;
		}
		else {
			this.addOrder('type_kid', 'SET', typeKid);
		}

		return this;
	}
}
